import { useUpdateStudentDifficultyMutation } from 'util/rtk-api'

import { ClassActionDropdown } from 'components/action-dropdown'
import { DifficultyDropdown } from 'components/tables/difficulty-dropdown'
import { GenericTable, NumberedView } from 'components/tables/generic-table'
import { useClassContext } from 'contexts/class-context'
import styles from 'css/components/tables/student-table.module.css'
import moreIconWhite from 'img/more-vert-white.svg'
import moreIcon from 'img/more-vert.svg'
import { useState, useMemo, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { StudentEntry } from '@all-for-one/shared-types'


const ActionCell = ({ classId, studentId, isSelected }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const navigate = useNavigate()
    const dropdownRef = useRef(null)

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen)
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            console.log('event.target', event.target)
            if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isDropdownOpen])

    return (
        <td>
            <div className={styles.actionCell} ref={dropdownRef}>
                <img 
                    src={isSelected ? moreIconWhite : moreIcon} 
                    alt="More"
                    onClick={toggleDropdown}
                />
                <div>
                {isDropdownOpen && (
                        <ClassActionDropdown onOpenStudent={() => navigate(`/classrooms/${classId}/students/${studentId}`)}/>
                )}
                </div>
            </div>
        </td>
    )
}

// Create a custom Students class that extends Array
class Students extends Array<StudentEntry> {
    filterByDifficulty(difficulty) {
        return difficulty === 'All' ? this : this.filter(student => student.difficulty === difficulty);
    }
}

type StudentTableProps = {
    keyFilter: Set<string>,
    style?: React.CSSProperties,
    clickableName?: boolean,
    renderHeader?: boolean,
    renderMoreActions?: boolean,
    currentDifficulty?: string
}

export const StudentTable = ({ keyFilter, style, clickableName = false, renderHeader = false, renderMoreActions = false, currentDifficulty }: StudentTableProps) => {
    const [currentTableDifficulty, setCurrentTableDifficulty] = useState(currentDifficulty || 'All')
    const { classId, students: studentData, updateStudentDifficulty: updateStudentDifficultyLocal } = useClassContext();
    const [ updateStudentDifficulty ] = useUpdateStudentDifficultyMutation()
    const navigate = useNavigate()
    
    // Update currentTableDifficulty when currentDifficulty prop changes
    useEffect(() => {
        if (currentDifficulty !== undefined) {
            setCurrentTableDifficulty(currentDifficulty)
        }
    }, [currentDifficulty])
    
    const safeStudents: StudentEntry[] = useMemo(() => Array.isArray(studentData) ? studentData : [], [studentData])
    const filteredStudents = useMemo(() => {
        const studentsArray = new Students(...safeStudents)
        return studentsArray.filterByDifficulty(currentTableDifficulty).map(student => ({
            ...student
        }))
    }, [currentTableDifficulty, safeStudents])

    const handleDifficultyChange = async (studentId: string, newStudentDifficulty) => {
        // Implement optimistic updating
        const currentStudent = safeStudents.find(s => s.studentId === studentId)
        const previousDifficulty = currentStudent.difficulty

        try{
            updateStudentDifficultyLocal(studentId, newStudentDifficulty)
            await updateStudentDifficulty({ classId, studentId, difficulty: newStudentDifficulty }).unwrap()

        } catch (error) {
            if (previousDifficulty) {
                updateStudentDifficultyLocal(studentId, previousDifficulty)
            }
        }
    }

    const columns = [
        {
            key: 'difficulty', header: 'Difficulty', className: styles.difficultyColumn, render: (difficulty, _, row) =>
                <DifficultyDropdown currentDifficulty={difficulty} handleDifficultyChange={(newDifficulty) => handleDifficultyChange(row.studentId, newDifficulty)} />
        },
        { key: 'studentName', header: 'Name', render: (name, index, row) =>
        clickableName ?
            <Link
                className={styles.lessonLink}
                to={`/classrooms/${classId}/students/${row.studentId}`}>
                <NumberedView index={index} value={row.studentName} />
            </Link>
        : <NumberedView index={index} value={row.studentName} />
        },
        { key: 'studentId', header: 'User ID', render: (id) => id || '-' },
        { key: 'grade', header: 'Grade' },
        {
            key: 'lessonAverage', header: 'Lesson Average. % ', render: (average) =>
                average != null ? Number(average).toFixed(1) : '-'
        }
        // ...add more columns as needed
    ].filter(column => keyFilter.has(column.key))

    const handleSelectStudent = (studentId) => {
        navigate(`/classrooms/${classId}/students/${studentId}`)
    }

    const actionCellForClass = (studentId, isSelected) => {
        return <ActionCell classId={classId} studentId={studentId} isSelected={isSelected} />
    }

    return (
        <div className={styles.studentTableContainer}>
            <GenericTable
                columns={columns}
                data={filteredStudents}
                rowKey="studentId"
                rowClassName={(_, idx) => idx % 2 === 0 ? styles.highlightRow : ''}
                style={style}
                renderHeader={renderHeader}
                renderMoreActions={renderMoreActions}
                createMoreActionCell={(studentId, isSelected) => actionCellForClass(studentId, isSelected)}
                doubleClickRowAction={handleSelectStudent}
            />
        </div>
    )
}

